/**
 * Pour compléter votre rituel — Maison Atlas
 * <cross-sell-rituel-component>: intercepts the mini "Ajouter" form submits
 * in the cross-sell cards and posts to /cart/add.js (Ajax API), so the
 * customer is NOT redirected to /cart. Dispatches the same 'cart:update'
 * event Vessel's cart drawer listens to, so the drawer auto-opens if
 * configured with the `auto-open` attribute.
 *
 * Visual feedback: button shows "✓" briefly on success, "!" on error,
 * then reverts to its original label.
 */

class CrossSellRituel extends HTMLElement {
  connectedCallback() {
    this.forms = this.querySelectorAll('.cross-sell-rituel__card-form');
    this.boundSubmit = this.handleSubmit.bind(this);
    this.forms.forEach((form) => form.addEventListener('submit', this.boundSubmit));
  }

  disconnectedCallback() {
    this.forms?.forEach((form) => form.removeEventListener('submit', this.boundSubmit));
  }

  async handleSubmit(event) {
    event.preventDefault();
    const form = event.currentTarget;
    const button = form.querySelector('.cross-sell-rituel__card-button');
    if (!button || button.disabled) return;

    const originalLabel = button.textContent.trim();
    const variantId = form.querySelector('input[name="id"]')?.value;
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    button.textContent = '…';

    try {
      const formData = new FormData(form);
      // Ask Shopify to also render commonly used cart sections so the
      // drawer / cart icon reflect the new state immediately (best-effort).
      formData.append('sections', 'cart-drawer,cart-icon');
      formData.append('sections_url', window.location.pathname);

      const response = await fetch('/cart/add.js', {
        method: 'POST',
        body: formData,
        headers: { Accept: 'application/javascript' },
      });

      if (!response.ok) {
        const errBody = await response.json().catch(() => ({}));
        throw new Error(errBody?.description || errBody?.message || 'Cart add failed');
      }

      // Fetch the latest cart state so we can broadcast item_count.
      let cart = null;
      try {
        const cartRes = await fetch('/cart.js', { headers: { Accept: 'application/javascript' } });
        cart = await cartRes.json();
      } catch (_) {
        /* non-fatal — drawer will fall back to its own state */
      }

      // Dispatch the event Vessel's <cart-drawer-component> listens to.
      document.dispatchEvent(
        new CustomEvent('cart:update', {
          bubbles: true,
          detail: {
            resource: cart,
            sourceId: 'cross-sell-rituel',
            data: {
              source: 'cross-sell',
              itemCount: cart?.item_count,
              variantId,
            },
          },
        })
      );

      // Visual confirmation in-button (no redirect).
      button.textContent = '✓';
      setTimeout(() => {
        button.textContent = originalLabel;
        button.disabled = false;
        button.removeAttribute('aria-busy');
      }, 1800);
    } catch (err) {
      console.error('[cross-sell-rituel] add failed', err);
      button.textContent = '!';
      setTimeout(() => {
        button.textContent = originalLabel;
        button.disabled = false;
        button.removeAttribute('aria-busy');
      }, 1800);
    }
  }
}

if (!customElements.get('cross-sell-rituel-component')) {
  customElements.define('cross-sell-rituel-component', CrossSellRituel);
}
